import diagnostics from "@/services/diagnostics";
import { ref } from "vue";
import { CRITICAL, openCritical } from "@/services/critical";

/**
 * Realtime — единственный WebSocket-клиент приложения (CONTRACT §3).
 *
 *   import realtime from '@/services/realtime'
 *   realtime.connect(url, token)               // App.vue после авторизации
 *   realtime.status.value                      // 'idle'|'connecting'|'open'|'reconnecting'|'closed'
 *   await realtime.whenOpen(10000)             // ждать открытия (reject по таймауту/закрытию)
 *   realtime.send({type:'leave_game', gameUUID}) // JSON; при переподключении — очередь (TTL 10 c)
 *   realtime.sendBinary(blob)                  // только при open; вернёт false, если не отправлено
 *   const off = realtime.on('game_status', (msg) => …); off()  // подписка по type
 *
 * Служебные события (через on()):
 *   'open'         — каждый успешный open (и первый, и после переподключения), payload {reconnected}
 *   'reconnected'  — open после обрыва: экраны должны пересинхронизироваться (например, sync_game)
 *   'disconnected' — сокет упал, идёт переподключение, payload {code, reason}
 *   'status'       — смена статуса, payload {status}
 *
 * Поведение:
 *  - heartbeat: {type:'ping', ts} сразу после open и каждые 20 c;
 *  - мёртвое соединение: нет ни одного входящего кадра 45 c → переподключение
 *    (сторож включается после первого pong, чтобы не зацикливаться со старым сервером);
 *  - переподключение с backoff 1, 2, 4, 8, 10, 10… c; если за ~45 c АКТИВНОГО времени (приложение на
 *    экране) не удалось — статус 'closed' и критический экран «Нет соединения с сервером». Время в фоне
 *    и заморозки JS в бюджет не входят, пока страница скрыта — переподключаемся без фатального исхода;
 *    возврат на экран и событие online обнуляют бюджет и backoff;
 *  - без переподключения: close() вручную; код 4001 (вход с другого устройства) и
 *    {type:'error', code:'ALREADY_CONNECTED'} → экран «Вы подключились с другого устройства»;
 *    коды 1008/4003 (авторизация) → «Сессия истекла»;
 *  - сообщения не накапливаются; логирование только в development.
 */

const HEARTBEAT_MS = 20000;
const DEAD_AFTER_MS = 45000;
const RECONNECT_BUDGET_MS = 45000;
const QUEUE_TTL_MS = 10000;
const CONNECT_TIMEOUT_MS = 10000;
const QUEUE_MAX = 50;
const BACKOFF_MS = [1000, 2000, 4000, 8000, 10000];
/** Больше одного обычного шага (backoff + таймаут соединения) за раз в бюджет не засчитывается. */
const BUDGET_STEP_MAX_MS = BACKOFF_MS[BACKOFF_MS.length - 1] + CONNECT_TIMEOUT_MS;

const CLOSE_REPLACED = 4001;
const AUTH_CLOSE_CODES = [1008, 4003];

const isDev = process.env.NODE_ENV === "development";
const log = (...args) => {
  if (isDev) console.log("[realtime]", ...args);
};

/** Текущий статус соединения (reactive). */
const status = ref("idle");

let socketFactory = (url) => new WebSocket(url);
let socket = null;
let baseUrl = null;
let token = null;
let everOpened = false;
let manualClose = false;
let attempt = 0;
let reconnectTimer = null;
let heartbeatTimer = null;
let watchdogTimer = null;
let connectTimer = null;
let lastInbound = 0;
let pongSeen = false;
let budgetUsed = 0; // активное время без соединения, мс
let budgetCheckAt = 0; // когда budgetUsed обновлялся (0 — отсчёт не идёт)
let queue = [];

const listeners = new Map(); // type → Set<cb>
let openWaiters = []; // {resolve, reject, timer}

diagnostics.registerSource("realtime", () => ({ status: status.value,
  streams: socket && socket.readyState < 2 ? 1 : 0, queued: queue.length, waiters: openWaiters.length,
  timers: [reconnectTimer, heartbeatTimer, watchdogTimer, connectTimer].filter(id => id !== null).length,
  subscriptions: [...listeners.values()].reduce((sum, set) => sum + set.size, 0) }));

function setStatus(next) {
  if (status.value === next) return;
  status.value = next;
  emit("status", { status: next });
}

function emit(type, payload) {
  const set = listeners.get(type);
  if (!set) return;
  [...set].forEach((cb) => {
    try {
      cb(payload);
    } catch (e) {
      console.error(`[realtime] listener "${type}" failed`, e);
    }
  });
}

function settleWaiters(error) {
  const waiters = openWaiters;
  openWaiters = [];
  waiters.forEach((w) => {
    clearTimeout(w.timer);
    if (error) w.reject(error);
    else w.resolve();
  });
}

function clearTimers() {
  clearInterval(heartbeatTimer);
  clearInterval(watchdogTimer);
  clearTimeout(reconnectTimer);
  clearTimeout(connectTimer);
  heartbeatTimer = null;
  watchdogTimer = null;
  reconnectTimer = null;
  connectTimer = null;
}

function detachSocket() {
  if (!socket) return;
  const old = socket;
  socket = null;
  old.onopen = null;
  old.onmessage = null;
  old.onerror = null;
  old.onclose = null;
  try {
    if (old.readyState === 0 || old.readyState === 1) old.close(1000, "client");
  } catch (e) {
    log("close failed", e);
  }
}

function pageHidden() {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/** Начать отсчёт бюджета переподключения заново (и backoff с первого шага). */
function resetBudget() {
  attempt = 0;
  budgetUsed = 0;
  budgetCheckAt = 0;
}

/**
 * Добавить в бюджет время с прошлой проверки — только если страница на экране, и не больше одного
 * обычного шага: заморозка/троттлинг таймеров в фоне не съедают бюджет.
 * @param {boolean} [visibleUntilNow] страница была на экране до этого момента (уход в фон прямо сейчас)
 * @returns {number} израсходованный бюджет, мс
 */
function accountBudget(visibleUntilNow = false) {
  const now = Date.now();
  if (budgetCheckAt && (visibleUntilNow || !pageHidden())) {
    budgetUsed += Math.min(Math.max(0, now - budgetCheckAt), BUDGET_STEP_MAX_MS);
  }
  budgetCheckAt = now;
  return budgetUsed;
}

function buildUrl() {
  if (!baseUrl) return null;
  const sep = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${sep}token=${encodeURIComponent(token || "")}`;
}

function rawSend(text) {
  if (!socket || socket.readyState !== 1) return false;
  try {
    socket.send(text);
    return true;
  } catch (e) {
    log("send failed", e);
    return false;
  }
}

function flushQueue() {
  const now = Date.now();
  const pending = queue;
  queue = [];
  pending.forEach((item) => {
    if (item.expires >= now) rawSend(item.text);
    else log("drop expired queued message", item.type);
  });
}

function startHeartbeat() {
  const ping = () => rawSend(JSON.stringify({ type: "ping", ts: Date.now() }));
  ping();
  heartbeatTimer = setInterval(ping, HEARTBEAT_MS);
  watchdogTimer = setInterval(() => {
    if (!pongSeen) return;
    if (Date.now() - lastInbound > DEAD_AFTER_MS) {
      log("no inbound frames for", DEAD_AFTER_MS, "ms → reconnect");
      handleDrop(4900, "dead connection");
    }
  }, 5000);
}

function openSocket() {
  const url = buildUrl();
  if (!url) return;
  detachSocket();
  let ws;
  try {
    ws = socketFactory(url);
  } catch (e) {
    log("socket factory failed", e);
    scheduleReconnect();
    return;
  }
  socket = ws;
  pongSeen = false;
  // Зависшее установление соединения (сокет так и не открылся и не закрылся) считаем обрывом.
  clearTimeout(connectTimer);
  connectTimer = setTimeout(() => {
    if (socket === ws && ws.readyState === 0) {
      log("connect timeout");
      handleDrop(4901, "connect timeout");
    }
  }, CONNECT_TIMEOUT_MS);
  ws.onopen = () => {
    if (socket !== ws) return;
    const reconnected = everOpened;
    everOpened = true;
    resetBudget();
    lastInbound = Date.now();
    clearTimers();
    setStatus("open");
    log(reconnected ? "reconnected" : "open");
    startHeartbeat();
    flushQueue();
    settleWaiters(null);
    emit("open", { reconnected });
    if (reconnected) emit("reconnected", {});
  };
  ws.onmessage = (event) => {
    if (socket !== ws) return;
    lastInbound = Date.now();
    if (typeof event.data !== "string") return; // сервер не шлёт бинарные кадры
    let message;
    try {
      message = JSON.parse(event.data);
    } catch (e) {
      log("non-JSON frame ignored");
      return;
    }
    if (!message || typeof message !== "object") return;
    if (message.type === "pong") {
      pongSeen = true;
      return;
    }
    if (message.type === "error" && message.code === "ALREADY_CONNECTED") {
      fatal(CRITICAL.REPLACED);
      return;
    }
    if (typeof message.type !== "string") return;
    if (isDev && message.type !== "ice_candidate") log("←", message.type, message.status || "");
    emit(message.type, message);
  };
  ws.onerror = () => {
    log("socket error");
  };
  ws.onclose = (event) => {
    if (socket !== ws) return;
    handleDrop(event.code, event.reason);
  };
}

/** Окончательно закрыть соединение и показать критический экран. */
function fatal(preset) {
  manualClose = true;
  clearTimers();
  detachSocket();
  queue = [];
  setStatus("closed");
  settleWaiters(Object.assign(new Error("Соединение закрыто"), { code: "WS_CLOSED" }));
  openCritical(preset);
}

function handleDrop(code, reason) {
  clearTimers();
  detachSocket();
  if (manualClose) {
    setStatus("closed");
    return;
  }
  log("closed", code, reason || "");
  if (code === CLOSE_REPLACED) {
    fatal(CRITICAL.REPLACED);
    return;
  }
  if (AUTH_CLOSE_CODES.includes(code)) {
    fatal(CRITICAL.SESSION_EXPIRED);
    return;
  }
  emit("disconnected", { code, reason });
  scheduleReconnect();
}

function scheduleReconnect() {
  clearTimeout(reconnectTimer);
  // Фатально — только на экране: в фоне сеть может быть урезана ОС, а пользователь не видит экрана.
  if (accountBudget() >= RECONNECT_BUDGET_MS && !pageHidden()) {
    log("reconnect budget exhausted");
    fatal(CRITICAL.OFFLINE);
    return;
  }
  setStatus(everOpened ? "reconnecting" : "connecting");
  const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
  attempt += 1;
  log(`reconnect #${attempt} in ${delay} ms`);
  reconnectTimer = setTimeout(openSocket, delay);
}

/** Переподключиться немедленно (сеть вернулась / вкладка снова видима). */
function reconnectNow() {
  if (manualClose || !baseUrl) return;
  if (status.value === "reconnecting" || (status.value === "connecting" && !socket)) {
    clearTimeout(reconnectTimer);
    openSocket();
  }
}

/** Сеть вернулась или приложение снова на экране: бюджет и backoff — с начала, попытка — сразу. */
function resumeReconnect() {
  if (manualClose || !baseUrl || status.value === "open") return;
  resetBudget();
  budgetCheckAt = Date.now();
  reconnectNow();
}

function onOnline() {
  resumeReconnect();
}

function onVisibility() {
  if (document.visibilityState !== "visible") {
    // Уходим в фон: засчитываем активное время до этого момента, фон в бюджет не войдёт.
    if (budgetCheckAt) accountBudget(true);
    return;
  }
  if (status.value === "open") {
    // Проверка живости после сна: таймеры в фоне троттлятся, поэтому даём 10 c на ответ (pong),
    // а не рвём соединение сразу. Если сокет полумёртвый — сторож переподключит.
    lastInbound = Math.max(lastInbound, Date.now() - DEAD_AFTER_MS + 10000);
    rawSend(JSON.stringify({ type: "ping", ts: Date.now() }));
  } else {
    resumeReconnect();
  }
}

let globalListenersAttached = false;
function attachGlobalListeners() {
  if (globalListenersAttached || typeof window === "undefined") return;
  globalListenersAttached = true;
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", onVisibility);
}

/* ------------------------------------------------------------- public API */

/**
 * Подключиться (идемпотентно для того же url+token).
 * @param {string} url  ws(s)://…/bunker/ws (без token)
 * @param {string} jwt  JWT, добавляется как ?token=
 */
function connect(url, jwt) {
  if (socket && baseUrl === url && token === jwt && (status.value === "open" || status.value === "connecting")) {
    return;
  }
  baseUrl = url;
  token = jwt;
  manualClose = false;
  resetBudget();
  budgetCheckAt = Date.now(); // бюджет переподключения считается и для первого подключения
  everOpened = false;
  clearTimers();
  attachGlobalListeners();
  setStatus("connecting");
  openSocket();
}

/** Намеренно закрыть соединение (без переподключения). */
function close() {
  manualClose = true;
  clearTimers();
  detachSocket();
  queue = [];
  setStatus("closed");
  settleWaiters(Object.assign(new Error("Соединение закрыто"), { code: "WS_CLOSED" }));
}

/**
 * Promise, который выполнится, когда соединение открыто.
 * Reject: {code:'WS_TIMEOUT'} по таймауту, {code:'WS_CLOSED'} если соединение закрыто окончательно.
 */
function whenOpen(timeoutMs = 10000) {
  if (status.value === "open" && socket && socket.readyState === 1) return Promise.resolve();
  if (status.value === "closed") {
    return Promise.reject(Object.assign(new Error("Соединение закрыто"), { code: "WS_CLOSED" }));
  }
  // 'idle' | 'connecting' | 'reconnecting' — ждём открытия
  return new Promise((resolve, reject) => {
    const waiter = { resolve, reject, timer: null };
    waiter.timer = setTimeout(() => {
      openWaiters = openWaiters.filter((w) => w !== waiter);
      reject(Object.assign(new Error("Нет соединения с сервером"), { code: "WS_TIMEOUT" }));
    }, timeoutMs);
    openWaiters.push(waiter);
  });
}

/**
 * Отправить JSON-сообщение.
 * @param {object} message
 * @param {{queue?: boolean, ttl?: number}} [options] queue=false — не ставить в очередь, если сокет не открыт
 * @returns {boolean} true — отправлено сразу или поставлено в очередь; false — отброшено
 */
function send(message, options = {}) {
  const { queue: allowQueue = true, ttl = QUEUE_TTL_MS } = options;
  const text = JSON.stringify(message);
  if (status.value === "open" && rawSend(text)) {
    if (isDev && message.type !== "ping" && message.type !== "ice_candidate") log("→", message.type);
    return true;
  }
  if (!allowQueue || manualClose || status.value === "closed") return false;
  queue.push({ text, type: message && message.type, expires: Date.now() + ttl });
  if (queue.length > QUEUE_MAX) queue.shift();
  return true;
}

/**
 * Отправить бинарный кадр (нарушение, CONTRACT §3.3). Только при открытом сокете.
 * @param {Blob|ArrayBuffer} data
 * @returns {boolean} отправлено ли
 */
function sendBinary(data) {
  if (status.value !== "open" || !socket || socket.readyState !== 1) return false;
  try {
    socket.send(data);
    return true;
  } catch (e) {
    log("binary send failed", e);
    return false;
  }
}

/** Подписка на сообщения сервера с данным type (или служебное событие). Возвращает функцию отписки. */
function on(type, cb) {
  if (!listeners.has(type)) listeners.set(type, new Set());
  listeners.get(type).add(cb);
  return () => {
    const set = listeners.get(type);
    if (set) set.delete(cb);
  };
}

/** Удалить выбранную очередь (например, сигналинг при выходе из игры). */
function dropQueued(predicate) {
  queue = queue.filter((item) => !predicate(item.type));
}

/** Подменить фабрику сокетов (dev mock). Вызывать до connect(). */
function setSocketFactory(factory) {
  socketFactory = factory;
}

/** true, если соединение сейчас открыто. */
function isOpen() {
  return status.value === "open";
}

const realtime = {
  status,
  connect,
  close,
  whenOpen,
  send,
  sendBinary,
  on,
  isOpen,
  reconnectNow,
  dropQueued,
  setSocketFactory,
};

export default realtime;
export { status, connect, close, whenOpen, send, sendBinary, on, isOpen, reconnectNow, dropQueued, setSocketFactory };
